import type { DataVisualization, TagValue, UserInstrumentClassificationDto } from "@eu.mdotm.api/sphere";
import { getThemeCssVars, qualifier, sumArrayLike } from "$root/utils";
import {
	type InnerRefProps,
	type StylableProps,
	Column,
	ComputedSizeContainer,
	Row,
	Svg,
	Text,
} from "@mdotm/shadcn/legacy/components";
import {
	type DataAttributesProps,
	unstyledFieldClassName,
	unstyledWrapperClassName,
	IconButton,
} from "@mdotm/shadcn/mdotm/components";
import { focusOnMount, overrideClassList, Switch, useUniqueDOMId } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { approxRound, mapBetweenRanges } from "@mdotm/shadcn/legacy/utils";
import { type KeyboardEventHandler, useMemo } from "react";
import { tagColorMap } from "../../colors";
import { TagBadge } from "./Tag";
import { PercentageInput } from "$root/components/PercentageInput";

export type TagDataCellProps = {
	values: Array<TagValue>;
	classification: UserInstrumentClassificationDto;
} & StylableProps &
	InnerRefProps<HTMLDivElement> &
	DataAttributesProps;

export function TagCell({ values, classification }: TagDataCellProps): React.JSX.Element {
	return (
		<Row alignItems="center" childrenGrow={1} className="h-full">
			<Switch
				case={classification.dataVisualization ?? "TAG_VIEW_WITHOUT_COLOR"}
				match={{
					BAR_CHART: () => <StackedTagChart values={values} options={classification.options} />,
					TAG_VIEW_WITH_COLOR: () => (
						<TagsAsBadges values={values} options={classification.options} viewMode="TAG_VIEW_WITH_COLOR" />
					),
					TAG_VIEW_WITHOUT_COLOR: () => (
						<TagsAsBadges values={values} options={classification.options} viewMode="TAG_VIEW_WITHOUT_COLOR" />
					),
				}}
			/>
		</Row>
	);
}

export type TagStackedChartProps = {
	values: Array<TagValue>;
	options: UserInstrumentClassificationDto["options"];
} & InnerRefProps<HTMLDivElement> &
	StylableProps;

export function StackedTagChart({ values, options, ...forward }: TagStackedChartProps): React.JSX.Element {
	const clipPathId = useUniqueDOMId();
	const sumOfWeights = sumArrayLike(values, (x) => x.weight ?? 0);
	const missingPointsTo100 = 100 - Math.min(sumOfWeights, 100);
	const filteredTagIdsWithUntagged = useMemo(() => {
		if (missingPointsTo100 < 0.01) {
			return values.map((x) => [x, false] as const);
		} else {
			return [...values.map((x) => [x, false] as const), [undefined, true] as const];
		}
	}, [missingPointsTo100, values]);

	return (
		<ComputedSizeContainer {...forward} className={overrideClassList("min-w-0", forward.className)}>
			{({ width }) =>
				values.length === 0 ? (
					<Text type="Body/S/Book" className="px-2" as="div">
						-
					</Text>
				) : (
					<Svg viewBox={{ height: 8, width }}>
						<defs>
							<clipPath id={clipPathId}>
								<rect x={0} y={2} width="100%" height={4} rx={2} />
							</clipPath>
						</defs>
						<g clipPath={`url(#${clipPathId})`}>
							<ForEach collection={filteredTagIdsWithUntagged} carry={{ x: 0 }}>
								{([tag, isUntagged], _, carry) => {
									const x = carry.x;
									const rectWidth = mapBetweenRanges(
										isUntagged ? missingPointsTo100 : (tag.weight ?? 0),
										0,
										Math.max(100, sumOfWeights),
										0,
										width,
									);
									carry.x += rectWidth;
									return (
										<rect
											x={x}
											y={2}
											width={rectWidth}
											height={4}
											fill={
												isUntagged
													? themeCSSVars.palette_N50
													: (tagColorMap[
															options?.find((o) => o.option === tag.option)?.color ?? "GRAY"
														]?.chartColor() ?? themeCSSVars.palette_N900)
											}
										/>
									);
								}}
							</ForEach>
						</g>
					</Svg>
				)
			}
		</ComputedSizeContainer>
	);
}

export type DataTagCellProps = {
	values: Array<TagValue>;
	options: UserInstrumentClassificationDto["options"];
	viewMode: Exclude<DataVisualization, "BAR_CHART">;
} & StylableProps &
	InnerRefProps<HTMLDivElement>;

export function TagsAsBadges({
	values,
	options,
	viewMode,
	className,
	style,
}: DataTagCellProps & StylableProps): React.JSX.Element {
	return (
		<Row
			minSize="0"
			gap={2}
			alignItems="center"
			className={overrideClassList("overflow-x-hidden", className)}
			style={style}
		>
			<ForEach collection={values}>
				{({ option }) => {
					const color = options?.find((x) => x.option === option)?.color;
					return (
						<TagBadge
							label={option ?? "-"}
							color={color ? tagColorMap[color].badgeColor() : getThemeCssVars(themeCSSVars.palette_N50)}
							grey={viewMode === "TAG_VIEW_WITHOUT_COLOR"}
						/>
					);
				}}
			</ForEach>
		</Row>
	);
}

export function TagWeightList({
	values,
	setValues,
	options,
	onKeyDown,
	showUntagged,
	visualization = "TAG_VIEW_WITH_COLOR",
}: {
	values: Array<TagValue>;
	options: UserInstrumentClassificationDto["options"];
	onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
	showUntagged?: boolean;
	setValues?(v: Array<TagValue>): void;
	visualization?: Extract<DataVisualization, "TAG_VIEW_WITH_COLOR" | "TAG_VIEW_WITHOUT_COLOR">;
}): React.JSX.Element {
	const readonly = !setValues;
	const entries = useMemo(() => {
		const sumOfWeights = approxRound(
			sumArrayLike(values, ({ weight }) => weight ?? 0),
			2,
		);
		const missingPointsTo100 = 100 - Math.min(sumOfWeights, 100);
		if (!showUntagged || missingPointsTo100 < 0.01) {
			return values.map((x) => [x, false] as const);
		} else {
			return [
				...values.map((x) => [x, false] as const),
				[{ option: "Untagged", weight: approxRound(missingPointsTo100, 2) }, true] as const,
			];
		}
	}, [showUntagged, values]);
	return (
		<Column childrenGrow={0} gap={8}>
			<ForEach collection={entries} keyProvider={([{ option }]) => option!}>
				{([{ option, weight }, isUntagged]) => {
					return (
						<Row
							alignItems="center"
							justifyContent="unset"
							className={`border-[color:var(--palette-N50)] border-b border-solid last:border-none last:pb-0 pb-2`}
						>
							{isUntagged ? (
								<Text type="Body/S/Book" className="!italic py-1">
									{option}
								</Text>
							) : (
								<TagBadge
									label={option ?? ""}
									color={
										visualization === "TAG_VIEW_WITHOUT_COLOR"
											? tagColorMap.GRAY.badgeColor()
											: tagColorMap[options?.find((x) => x.option === option)?.color ?? "GRAY"].badgeColor()
									}
								/>
							)}
							<Row flexGrow={0} alignItems="center" className="ml-auto">
								<PercentageInput
									innerRef={isUntagged ? undefined : focusOnMount}
									value={weight ?? null}
									onChange={
										isUntagged
											? undefined
											: (newWeight) => {
													setValues?.([
														...values.filter((o) => o.option !== option),
														{
															option,
															weight: newWeight ?? undefined,
														},
													]);
												}
									}
									inputAppearance={{
										className: overrideClassList(unstyledFieldClassName, "focus-visible:outline-hidden"),
									}}
									className={unstyledWrapperClassName}
									readOnly={readonly || isUntagged}
									size="x-small"
									onKeyDown={onKeyDown}
								/>
								{!readonly && (
									<IconButton
										icon="Close"
										onClick={
											readonly || isUntagged
												? undefined
												: () => {
														setValues?.(values.filter((x) => x.option !== option));
													}
										}
										size="14"
										color={themeCSSVars.palette_N600}
										className={{
											"p-0.5! -m-0.5!": true,
											"opacity-0 pointer-events-none": isUntagged,
										}}
										data-qualifier={qualifier.component.button(`Delete/Option/${option}`)}
									/>
								)}
							</Row>
						</Row>
					);
				}}
			</ForEach>
		</Column>
	);
}
